/**
 * StandardApplyWizard — apply a resolved standard schedule onto an EPC project.
 * Resolves the project's category/capacity → standard, then runs a debounced,
 * write-free preview as the planner tunes anchor date, pruned branches, discipline
 * adjustment factors and per-task / bulk-BOQ quantity overrides. The dated preview
 * tree, block panel (capacity/org-unit) and warnings all react live; Apply commits
 * with the full body + the preview's applyId (server recomputes the gate). When the
 * project already has a snapshot the commit stages for the compare gate instead of
 * applying directly.
 * Visual register: Sovereign Emerald (Antd 6 — styles.{header,body,footer}).
 */
import {
    Alert,
    Button,
    DatePicker,
    Empty,
    InputNumber,
    Modal,
    Select,
    Spin,
    Tooltip,
    message,
} from 'antd';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { ClipboardPaste, Plus, Sparkles, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import {
    useCommitStandardApply,
    usePreviewStandardApply,
    useResolveStandardApply,
} from '../hooks/useStandardApply';
import type { ApplyPreview, ApplyPreviewBody, ApplyPreviewTask } from '../types';
import { ApplyPreviewTree } from './components/ApplyPreviewTree';
import { ApplyWarningsPanel } from './components/ApplyWarningsPanel';
import { QtyOverrideGrid } from './components/QtyOverrideGrid';

export interface StandardApplyWizardProps {
    open: boolean;
    projectId: string;
    onClose: () => void;
    /** compare-gate path: the staged import to route the planner to. */
    onStaged: (stagedImportId: string) => void;
    /** direct-ready path: the schedule was materialised in place. */
    onApplied: () => void;
}

function apiErrMsg(err: unknown, fallback: string): string {
    const msg = (err as { response?: { data?: { message?: string } } } | undefined)?.response
        ?.data?.message;
    return msg || fallback;
}

function errCode(err: unknown): string | undefined {
    return (err as { response?: { data?: { code?: string } } } | undefined)?.response?.data?.code;
}

const CAPTION = 'mb-2 text-[10px] font-bold uppercase tracking-[0.12em]';

interface AdjustmentRow {
    disciplineKey: string;
    factor: number;
}

export function StandardApplyWizard({
    open,
    projectId,
    onClose,
    onStaged,
    onApplied,
}: StandardApplyWizardProps) {
    const resolveQ = useResolveStandardApply(projectId, open);
    const previewMut = usePreviewStandardApply(projectId);
    const commitMut = useCommitStandardApply(projectId);

    const resolved = resolveQ.data?.resolved ?? null;

    // ── Options state ──────────────────────────────────────────────────────
    const [anchorDate, setAnchorDate] = useState<string | undefined>(undefined);
    const [prunedKeys, setPrunedKeys] = useState<Set<string>>(new Set());
    const [adjustmentFactors, setAdjustmentFactors] = useState<AdjustmentRow[]>([]);
    const [qtyOverrides, setQtyOverrides] = useState<Map<string, number>>(new Map());
    const [preview, setPreview] = useState<ApplyPreview | null>(null);
    const [bomOpen, setBomOpen] = useState(false);

    const editProjectHref = `/epc/projects/${projectId}/edit`;

    // ── Reset all state on close ───────────────────────────────────────────
    const sentKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (open) return;
        sentKeyRef.current = null;
        setAnchorDate(undefined);
        setPrunedKeys(new Set());
        setAdjustmentFactors([]);
        setQtyOverrides(new Map());
        setPreview(null);
        setBomOpen(false);
    }, [open]);

    // ── Assemble the preview/commit body from the current options ──────────
    const body = useMemo<ApplyPreviewBody | null>(() => {
        if (!resolved) return null;
        return {
            standardId: resolved.standardId,
            standardVersion: resolved.standardVersion,
            ...(anchorDate ? { anchorDate } : {}),
            ...(prunedKeys.size > 0 ? { prunedKeys: [...prunedKeys].sort() } : {}),
            ...(adjustmentFactors.length > 0
                ? {
                      adjustmentFactors: adjustmentFactors
                          .filter((a) => a.disciplineKey && Number.isFinite(a.factor))
                          .map((a) => ({ disciplineKey: a.disciplineKey, factor: a.factor })),
                  }
                : {}),
            ...(qtyOverrides.size > 0
                ? {
                      qtyOverrides: [...qtyOverrides.entries()]
                          .sort((a, b) => a[0].localeCompare(b[0]))
                          .map(([taskKey, qty]) => ({ taskKey, qty })),
                  }
                : {}),
        };
    }, [resolved, anchorDate, prunedKeys, adjustmentFactors, qtyOverrides]);

    // ── Debounced preview on any option change (400ms) ─────────────────────
    // biome-ignore lint/correctness/useExhaustiveDependencies: mutation objects are unstable per render; mutateAsync is stable
    useEffect(() => {
        if (!open || !body) return;
        const key = JSON.stringify(body);
        if (sentKeyRef.current === key) return; // identical body already previewed
        const timer = window.setTimeout(async () => {
            try {
                const p = await previewMut.mutateAsync(body);
                sentKeyRef.current = key;
                setPreview(p);
            } catch (e) {
                setPreview(null);
                message.error(apiErrMsg(e, 'Preview failed'));
            }
        }, 400);
        return () => window.clearTimeout(timer);
    }, [open, body]);

    const runPreview = async (nextBody: ApplyPreviewBody) => {
        try {
            const p = await previewMut.mutateAsync(nextBody);
            sentKeyRef.current = JSON.stringify(nextBody);
            setPreview(p);
        } catch (e) {
            setPreview(null);
            message.error(apiErrMsg(e, 'Preview failed'));
        }
    };

    // ── Option handlers ────────────────────────────────────────────────────
    /** key + every descendant key (pruning a summary prunes its subtree). */
    const subtreeKeys = (tasks: ApplyPreviewTask[], rootKey: string): string[] => {
        const byParent = new Map<string, string[]>();
        for (const t of tasks) {
            if (t.parentKey !== null)
                byParent.set(t.parentKey, [...(byParent.get(t.parentKey) ?? []), t.key]);
        }
        const out: string[] = [];
        const stack = [rootKey];
        while (stack.length > 0) {
            const k = stack.pop() as string;
            if (out.includes(k)) continue;
            out.push(k);
            stack.push(...(byParent.get(k) ?? []));
        }
        return out;
    };

    const handleTogglePrune = (task: ApplyPreviewTask, pruned: boolean) => {
        const keys = task.isSummary && preview ? subtreeKeys(preview.tasks, task.key) : [task.key];
        setPrunedKeys((prev) => {
            const next = new Set(prev);
            for (const k of keys) {
                if (pruned) next.add(k);
                else next.delete(k);
            }
            return next;
        });
    };

    const handleQtyOverride = (taskKey: string, qty: number | undefined) => {
        setQtyOverrides((prev) => {
            const next = new Map(prev);
            if (qty === undefined) next.delete(taskKey);
            else next.set(taskKey, qty);
            return next;
        });
    };

    const handleBomApply = (overrides: { taskKey: string; qty: number }[]) => {
        setQtyOverrides((prev) => {
            const next = new Map(prev);
            for (const o of overrides) next.set(o.taskKey, o.qty);
            return next;
        });
        message.success(
            `${overrides.length} ${overrides.length === 1 ? 'override' : 'overrides'} applied from BOQ`,
        );
    };

    const leafKeys = useMemo(
        () => (preview ? preview.tasks.filter((t) => !t.isSummary && t.qty).map((t) => t.key) : []),
        [preview],
    );
    const disciplineKeys = useMemo(
        () =>
            preview
                ? [
                      ...new Set(
                          preview.tasks
                              .map((t) => t.disciplineKey)
                              .filter((d): d is string => !!d),
                      ),
                  ].sort()
                : [],
        [preview],
    );
    const leafCount = leafKeys.length;

    // ── Commit ─────────────────────────────────────────────────────────────
    const handleApply = async () => {
        if (!preview || !body || preview.blocks.length > 0) return;
        try {
            const res = await commitMut.mutateAsync({ applyId: preview.applyId, ...body });
            if (res.staged && res.stagedImportId) {
                message.success('Schedule staged — review changes');
                onStaged(res.stagedImportId);
            } else {
                const created = res.applyRun?.result?.mappingsCreated;
                message.success(
                    `Standard applied${created !== undefined ? ` — ${created} tasks, baseline day plans generated` : ''}`,
                );
                onApplied();
            }
            onClose();
        } catch (e) {
            const code = errCode(e);
            if (isAxiosError(e) && e.response?.status === 409) {
                if (code === 'APPLY_IN_PROGRESS') {
                    message.error('Another apply is already in progress on this project.');
                    return;
                }
                // APPLY_STALE (or any other 409): inputs drifted — re-preview.
                message.warning('Inputs changed — preview refreshed');
                sentKeyRef.current = null;
                if (body) await runPreview(body);
                return;
            }
            if (isAxiosError(e) && e.response?.status === 422) {
                // The block list lives in the refreshed preview; surface the server message too.
                message.error(apiErrMsg(e, 'This standard cannot be applied — resolve the blocks.'));
                sentKeyRef.current = null;
                if (body) await runPreview(body);
                return;
            }
            message.error(apiErrMsg(e, 'Apply failed'));
        }
    };

    const previewing = previewMut.isPending;
    const committing = commitMut.isPending;
    const blocked = !!preview && preview.blocks.length > 0;
    const canApply = !!preview && !blocked && !committing;
    const applyLabel = preview?.existingSnapshot ? 'Stage for review' : 'Apply standard schedule';

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={1100}
            destroyOnHidden
            maskClosable={false}
            title={
                <span className="inline-flex items-center gap-2">
                    <Sparkles size={16} aria-hidden style={{ color: SOVEREIGN.brand }} />
                    <span className="text-[15px] font-bold" style={{ color: SOVEREIGN.title }}>
                        Apply standard schedule
                    </span>
                    {resolved && (
                        <span
                            className="text-[11.5px] font-semibold"
                            style={{ color: SOVEREIGN.kpiCaption }}
                        >
                            {resolved.name} · v{resolved.standardVersion} · {resolved.leafCount}{' '}
                            tasks
                        </span>
                    )}
                </span>
            }
            styles={{
                header: {
                    background: SOVEREIGN.canvas,
                    borderBottom: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingBottom: 12,
                },
                body: {
                    background: SOVEREIGN.paper,
                    padding: 20,
                    maxHeight: '72vh',
                    overflowY: 'auto',
                },
                footer: {
                    background: SOVEREIGN.canvas,
                    borderTop: `1px solid ${SOVEREIGN.cardBorder}`,
                    paddingTop: 12,
                },
            }}
            footer={
                <div className="flex items-center justify-between gap-3">
                    <span
                        className="text-[11.5px] font-semibold tabular-nums"
                        style={{ color: SOVEREIGN.kpiCaption }}
                    >
                        {preview
                            ? `${leafCount} ${leafCount === 1 ? 'task' : 'tasks'} · ${preview.planStart} → ${preview.planFinish}`
                            : ''}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={committing}
                            disabled={!canApply}
                            onClick={() => void handleApply()}
                            style={
                                canApply
                                    ? { background: SOVEREIGN.btn, borderColor: SOVEREIGN.btnBorder }
                                    : undefined
                            }
                        >
                            {applyLabel}
                        </Button>
                    </div>
                </div>
            }
        >
            {resolveQ.isPending ? (
                <div className="flex justify-center py-16">
                    <Spin />
                </div>
            ) : resolveQ.isError ? (
                <Alert
                    type="error"
                    showIcon
                    message="Could not resolve a standard for this project"
                    description={apiErrMsg(
                        resolveQ.error,
                        'Standard schedules may not be enabled on this environment, or the project is missing a category.',
                    )}
                />
            ) : !resolved ? (
                <div
                    className="rounded-2xl py-10"
                    style={{
                        background: SOVEREIGN.cardBg,
                        border: `1.5px solid ${SOVEREIGN.cardBorder}`,
                    }}
                >
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <span style={{ color: SOVEREIGN.ink }}>
                                {resolveQ.data?.reason ??
                                    'No published standard matches this project’s category and capacity.'}
                                <br />
                                <Link
                                    to={editProjectHref}
                                    style={{ color: SOVEREIGN.brand, fontWeight: 600 }}
                                >
                                    Set the project category & capacity
                                </Link>
                            </span>
                        }
                    />
                </div>
            ) : (
                <div className="space-y-5">
                    {/* ── Options ─────────────────────────────────────────── */}
                    <section
                        className="rounded-2xl p-4"
                        style={{
                            background: SOVEREIGN.cardBg,
                            border: `1.5px solid ${SOVEREIGN.cardBorder}`,
                            boxShadow: SOVEREIGN.cardShadow,
                        }}
                    >
                        <div className="flex flex-wrap items-end gap-5">
                            <div>
                                <div
                                    className="mb-1 text-[11px] font-semibold"
                                    style={{ color: SOVEREIGN.ink }}
                                >
                                    Anchor date
                                </div>
                                <DatePicker
                                    aria-label="Anchor date"
                                    value={anchorDate ? dayjs(anchorDate) : null}
                                    onChange={(d) =>
                                        setAnchorDate(d ? d.format('YYYY-MM-DD') : undefined)
                                    }
                                    format="DD-MM-YYYY"
                                    allowClear
                                    placeholder="Project start"
                                    style={{
                                        width: 180,
                                        background: SOVEREIGN.inputBg,
                                        borderColor: SOVEREIGN.inputBorder,
                                    }}
                                />
                                <div
                                    className="mt-1 text-[10.5px]"
                                    style={{ color: SOVEREIGN.kpiCaption }}
                                >
                                    Blank uses the project start date.
                                </div>
                            </div>
                            <Button
                                icon={<ClipboardPaste size={14} aria-hidden />}
                                onClick={() => setBomOpen(true)}
                                disabled={!preview}
                                style={{ borderColor: SOVEREIGN.btnBorder, color: SOVEREIGN.bottle }}
                            >
                                Paste from BOQ
                            </Button>
                            {qtyOverrides.size > 0 && (
                                <span
                                    className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold"
                                    style={{ color: SOVEREIGN.bottle }}
                                >
                                    {qtyOverrides.size} qty{' '}
                                    {qtyOverrides.size === 1 ? 'override' : 'overrides'}
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<Trash2 size={13} aria-hidden />}
                                        aria-label="Clear all quantity overrides"
                                        onClick={() => setQtyOverrides(new Map())}
                                    />
                                </span>
                            )}
                            {previewing && (
                                <span
                                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold"
                                    style={{ color: SOVEREIGN.kpiCaption }}
                                >
                                    <Spin size="small" /> Updating preview…
                                </span>
                            )}
                        </div>

                        {/* Adjustment factors */}
                        {disciplineKeys.length > 0 && (
                            <div className="mt-4">
                                <div className={CAPTION} style={{ color: SOVEREIGN.kpiCaption }}>
                                    Discipline adjustment factors
                                </div>
                                <div className="space-y-2">
                                    {adjustmentFactors.map((row, idx) => (
                                        // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and editable in place
                                        <div key={idx} className="flex items-center gap-2">
                                            <Select
                                                aria-label="Discipline"
                                                size="small"
                                                value={row.disciplineKey || undefined}
                                                placeholder="Discipline"
                                                style={{ width: 200 }}
                                                options={disciplineKeys.map((d) => ({
                                                    value: d,
                                                    label: d,
                                                }))}
                                                onChange={(v) =>
                                                    setAdjustmentFactors((prev) =>
                                                        prev.map((r, i) =>
                                                            i === idx
                                                                ? { ...r, disciplineKey: v }
                                                                : r,
                                                        ),
                                                    )
                                                }
                                            />
                                            <span
                                                className="text-[11px] font-semibold"
                                                style={{ color: SOVEREIGN.kpiCaption }}
                                            >
                                                ×
                                            </span>
                                            <InputNumber
                                                aria-label="Factor"
                                                size="small"
                                                min={0.001}
                                                step={0.1}
                                                value={row.factor}
                                                onChange={(v) =>
                                                    setAdjustmentFactors((prev) =>
                                                        prev.map((r, i) =>
                                                            i === idx
                                                                ? {
                                                                      ...r,
                                                                      factor:
                                                                          typeof v === 'number'
                                                                              ? v
                                                                              : 1,
                                                                  }
                                                                : r,
                                                        ),
                                                    )
                                                }
                                                style={{ width: 96 }}
                                            />
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<Trash2 size={13} aria-hidden />}
                                                aria-label="Remove adjustment factor"
                                                onClick={() =>
                                                    setAdjustmentFactors((prev) =>
                                                        prev.filter((_, i) => i !== idx),
                                                    )
                                                }
                                            />
                                        </div>
                                    ))}
                                    <Button
                                        type="dashed"
                                        size="small"
                                        icon={<Plus size={13} aria-hidden />}
                                        onClick={() =>
                                            setAdjustmentFactors((prev) => [
                                                ...prev,
                                                { disciplineKey: '', factor: 1 },
                                            ])
                                        }
                                    >
                                        Add adjustment
                                    </Button>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ── Blocks ──────────────────────────────────────────── */}
                    {blocked && preview && (
                        <Alert
                            type="error"
                            showIcon
                            icon={<TriangleAlert size={16} aria-hidden />}
                            message={`This standard cannot be applied — ${preview.blocks.length} ${preview.blocks.length === 1 ? 'block' : 'blocks'} to resolve`}
                            description={
                                <>
                                    <ul className="m-0 max-h-40 list-disc overflow-y-auto pl-5">
                                        {preview.blocks.map((b) => (
                                            <li key={b} className="text-[12px]">
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                    <Link
                                        to={editProjectHref}
                                        className="mt-1 inline-block text-[12px]"
                                        style={{ color: SOVEREIGN.brand, fontWeight: 600 }}
                                    >
                                        Fix the project’s capacity & org units
                                    </Link>
                                </>
                            }
                        />
                    )}

                    {/* ── Warnings ────────────────────────────────────────── */}
                    {preview && <ApplyWarningsPanel warnings={preview.warnings} />}

                    {/* ── Preview tree ────────────────────────────────────── */}
                    <section>
                        <div className="mb-2 flex items-center justify-between">
                            <div className={CAPTION} style={{ color: SOVEREIGN.kpiCaption }}>
                                Preview
                            </div>
                            {preview && (
                                <span
                                    className="text-[11px] font-semibold tabular-nums"
                                    style={{ color: SOVEREIGN.kpiCaption }}
                                >
                                    capacity DC {preview.capacity.dc ?? '—'} · AC{' '}
                                    {preview.capacity.ac ?? '—'}
                                </span>
                            )}
                        </div>
                        <div
                            className="rounded-2xl p-2"
                            style={{
                                background: SOVEREIGN.cardBg,
                                border: `1.5px solid ${SOVEREIGN.cardBorder}`,
                                boxShadow: SOVEREIGN.cardShadow,
                            }}
                        >
                            {preview ? (
                                <ApplyPreviewTree
                                    tasks={preview.tasks}
                                    prunedKeys={prunedKeys}
                                    onTogglePrune={handleTogglePrune}
                                    onQtyOverride={handleQtyOverride}
                                />
                            ) : (
                                <div className="flex justify-center py-12">
                                    <Spin />
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            )}

            <QtyOverrideGrid
                open={bomOpen}
                leafKeys={leafKeys}
                onClose={() => setBomOpen(false)}
                onApply={handleBomApply}
            />
        </Modal>
    );
}

export default StandardApplyWizard;
